import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  src: string
  /** Inclusive start of the playable window, in seconds (0 = beginning of file). */
  startSec?: number
  /** Inclusive end of the playable window, in seconds. Required to clip. */
  endSec?: number
  className?: string
}

/**
 * HTML5 `<audio>` wrapper that, when `startSec`/`endSec` are set, restricts
 * playback to that window:
 *   - On metadata load: seeks to `startSec`.
 *   - On `timeupdate`: pauses + rewinds to `startSec` once `currentTime ≥ endSec`.
 *   - On user seek: clamps the cursor inside `[startSec, endSec]`.
 *
 * The audio file itself is untouched — we only steer the playhead. Native
 * browser controls remain visible; the user just can't escape the bounds.
 *
 * If `startSec` and `endSec` are both unset, behaves like a plain `<audio>`.
 */
export function ClippedAudio({ src, startSec, endSec, className }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isClipped = typeof startSec === 'number' && typeof endSec === 'number' && endSec > startSec

  useEffect(() => {
    const el = audioRef.current
    if (!el || !isClipped) return
    const start = startSec!
    const end = endSec!

    // Seek to start once we know the file is long enough to handle it.
    function onMeta() {
      try { el!.currentTime = start } catch { /* race on quick remount */ }
    }
    // Stop at the end and rewind so a second play starts at the clip start.
    function onTime() {
      if (el!.currentTime >= end) {
        el!.pause()
        try { el!.currentTime = start } catch { /* ignore */ }
      }
    }
    // Native scrubber would let the user escape the window — clamp it back.
    function onSeeking() {
      if (el!.currentTime < start) el!.currentTime = start
      else if (el!.currentTime > end) el!.currentTime = end
    }

    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('seeking', onSeeking)
    // If metadata is already there (cached file), apply immediately.
    if (el.readyState >= 1) onMeta()
    return () => {
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('seeking', onSeeking)
    }
  }, [src, startSec, endSec, isClipped])

  return (
    <audio
      ref={audioRef}
      src={src}
      controls
      preload="metadata"
      className={cn('block h-9 w-full', className)}
    />
  )
}
